import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { queryClient } from '../lib/queryClient';
import type { EventsRole } from '../types/eventsRole';

// Define the types for the context value
interface AuthContextType {
  accessToken: string | null; // Store the access token
  user: UserInfo | null; // Store user information
  loading: boolean; // Loading state
  error: string | null; // Error message
  /**
   * True only when the authoritative /api/stores/user-info/ endpoint
   * responded successfully in this session. When false, group-billing flags
   * are unknown (cached or legacy data) and billing mutations must stay
   * hidden (fail closed).
   */
  userInfoSettled: boolean;
  login: (
    email: string,
    password: string,
    isChecked: boolean,
  ) => Promise<{ success: boolean; error?: string }>; // Login function
  logout: () => void; // Logout function
  refreshToken: () => Promise<UserInfo | null>; // Refresh token function
  refreshUser: () => Promise<void>; // Re-fetch user info (e.g. after store creation)
}

interface UserInfo {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  store_id?: number;
  store_name?: string;
  role: 'Owner' | 'Manager' | 'Staff';
  is_platform_admin?: boolean;
  is_store_group_user?: boolean;
  available_stores?: Array<{ id: number; name: string }>;
  show_sms_on_dashboard?: boolean;
  manual_menu_snoozing_enabled?: boolean;
  takes_events?: boolean;
  takes_catering?: boolean;
  events_platform_enabled?: boolean;
  // Summary of the events role for the selected store. Authorization inside
  // /events always comes from GET /api/events/session/ instead.
  events_role?: EventsRole | null;
  // Group billing context (authoritative endpoint only)
  store_group_id?: number | null;
  store_group_name?: string | null;
  billing_managed_at_group_level?: boolean;
  can_manage_group_billing?: boolean;
  manageable_billing_groups?: Array<{ id: number; name: string }>;
  /** Groups this user adds locations to: the Onboarding page. */
  onboarding_groups?: Array<{ id: number; name: string }>;
  /** Announcement keys the user has not dismissed (authoritative endpoint only). */
  pending_announcements?: string[];
}

// Create context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

const baseURL = process.env.REACT_APP_API_BASE_URL;

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    console.error('Error parsing token:', e);
    return true; // If there's an error, treat as expired
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('access_token'),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfoSettled, setUserInfoSettled] = useState<boolean>(false);
  const [user, setUser] = useState<UserInfo | null>(() => {
    // Initialize user from localStorage if available
    try {
      const cachedUser = localStorage.getItem('user_info');
      if (cachedUser) {
        const userData = JSON.parse(cachedUser);
        console.log('Loaded cached user data:', userData);
        return userData;
      }
    } catch (error) {
      console.error(
        'Error parsing cached user data during initialization:',
        error,
      );
      localStorage.removeItem('user_info');
    }
    return null;
  });

  const refreshToken = async (): Promise<string | null> => {
    try {
      const response = await fetch(`${baseURL}/api/user/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh: localStorage.getItem('refresh_token'),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
        return data.access;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  const fetchUserInfo = async (token: string) => {
    // Whether the response came from the authoritative endpoint. Legacy
    // responses lack the group-billing flags, so consumers must fail closed.
    let authoritative = true;
    try {
      let response;

      // Try the stores user-info endpoint first (supports platform admin)
      try {
        const storeId = localStorage.getItem('currentStoreId');
        response = await fetch(`${baseURL}/api/stores/user-info/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(storeId && { 'X-Store-ID': storeId }),
          },
        });

        // Fall back to the old endpoint ONLY when the new one doesn't exist
        // (compatibility 404). Other errors surface as errors so we never
        // interpret an unknown group-billing flag as "not managed".
        if (response.status === 404) {
          console.log('Falling back to legacy user-info endpoint');
          authoritative = false;
          response = await fetch(`${baseURL}/api/user-info/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
      } catch (fetchError) {
        // If the new endpoint fails (network error, CORS, etc.), try the old one
        console.log('New endpoint failed, using legacy endpoint:', fetchError);
        authoritative = false;
        response = await fetch(`${baseURL}/api/user-info/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.status === 401) {
        // Token might be expired, try to refresh
        const newToken = await refreshToken();
        if (newToken) {
          return await fetchUserInfo(newToken);
        }
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch user info');
      }

      const data = await response.json();
      setUser(data);
      setUserInfoSettled(authoritative);
      localStorage.setItem('user_info', JSON.stringify(data));

      // Ensure currentStoreId is set (only if not already set by store selector)
      const existingStoreId = localStorage.getItem('currentStoreId');
      console.log(
        'AuthContext: fetchUserInfo - existing currentStoreId:',
        existingStoreId,
      );
      console.log('AuthContext: fetchUserInfo - user.store_id:', data.store_id);

      // No fallback store id: a group-billing owner legitimately has no store,
      // and guessing "1" would point their requests at someone else's store.
      if (!data.store_id) {
        console.log('AuthContext: No store_id on user; leaving store unset');
        localStorage.removeItem('currentStoreId');
      } else if (!existingStoreId) {
        console.log(
          'AuthContext: Setting currentStoreId to user.store_id:',
          data.store_id,
        );
        localStorage.setItem('currentStoreId', data.store_id.toString());
      } else {
        console.log(
          'AuthContext: Keeping existing currentStoreId:',
          existingStoreId,
        );
      }

      setError(null);
      return data;
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserInfoSettled(false);
      setError(error instanceof Error ? error.message : 'An error occurred');
      if (error instanceof Error && error.message.includes('Session expired')) {
        logout();
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setLoading(true);

    if (token) {
      if (isTokenExpired(token)) {
        console.log('Token expired, attempting to refresh...');
        refreshToken().then((newToken) => {
          if (newToken) {
            setAccessToken(newToken);
            fetchUserInfo(newToken);
          } else {
            setLoading(false);
            logout();
          }
        });
      } else {
        setAccessToken(token);
        fetchUserInfo(token);
      }
    } else {
      console.log('No token found, clearing user data');
      queryClient.clear();
      setAccessToken(null);
      setUser(null);
      setLoading(false);
      localStorage.removeItem('user_info');
    }
  }, []);

  const login = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${baseURL}/api/user/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail || 'Login failed. Please check your credentials.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      const data = await response.json();
      queryClient.clear();
      localStorage.setItem('access_token', data.access);
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }
      setAccessToken(data.access);

      // Fetch user info with the new token
      const userInfo = await fetchUserInfo(data.access);
      if (userInfo) {
        // Store the store_id in localStorage for API requests. Group-billing
        // owners have no store, so leave it unset rather than guessing.
        if (userInfo.store_id) {
          localStorage.setItem('currentStoreId', userInfo.store_id.toString());
        } else {
          localStorage.removeItem('currentStoreId');
        }
        return { success: true };
      }
      return { success: false, error: 'Failed to load user information' };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    queryClient.clear();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('currentStoreId');
    setAccessToken(null);
    setUser(null);
    setError(null);
    setUserInfoSettled(false);
    console.log('User logged out');
  };

  const refreshUser = async () => {
    if (accessToken) {
      await fetchUserInfo(accessToken);
    }
  };

  const value = {
    accessToken,
    user,
    loading,
    error,
    userInfoSettled,
    login,
    logout,
    refreshUser,
    refreshToken: () =>
      refreshToken().then((token) => {
        if (token) {
          setAccessToken(token);
          return fetchUserInfo(token);
        }
        return null;
      }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
